// Legend — spells out what the EA/GA and Beta/Stable chips on cards mean.
// Shown inside the Timeline's Glossary ("Definitions") drawer so every
// audience always has the definitions within reach.
//
// Two independent axes answer different questions:
//   - Access (EA / GA)      — who can use it right now
//   - Readiness (Beta/Stable) — is it done, or still evolving
//
// Because they move independently, every launch lands in one of four
// states. The 2×2 below spells each out with a plain definition, a
// customer talk-track ("Say"), and the example feature — so the label a
// customer hears ("beta", "early access") is unambiguous.
//
// `bare` drops the card chrome (border/bg/padding) for hosts that supply
// their own — e.g. the Glossary drawer.

interface Combo {
  access: { label: string; cls: string };
  readiness: { label: string; cls: string };
  def: string;
  say: string;
  example: string;
}

const EA = { label: "EA", cls: "bg-ea-soft text-ea" };
const GA = { label: "GA", cls: "bg-ga-soft text-ga" };
const BETA = { label: "Beta", cls: "bg-minor-soft text-minor" };
const STABLE = { label: "Stable", cls: "bg-border text-text-secondary" };

// Order reads down-then-across the 2×2 (EA row, then GA row) so it tracks a
// feature's usual path: earliest stage → fully shipped.
const COMBOS: Combo[] = [
  {
    access: EA,
    readiness: BETA,
    def: "Limited rollout, still has gaps.",
    say: "Design partners / opt-ins only. Be direct about the rough edges — no firm dates.",
    example: "Gemini Proc Gen",
  },
  {
    access: EA,
    readiness: STABLE,
    def: "Works and complete — just a staged rollout.",
    say: "Confident for those who have it; “coming to your workspace” for those who don’t.",
    example: "Face Blurring",
  },
  {
    access: GA,
    readiness: BETA,
    def: "On for everyone; the beta label manages the gaps.",
    say: "Demo and encourage adoption — just don’t present it as finished.",
    example: "Custom Instructions for Proc Gen",
  },
  {
    access: GA,
    readiness: STABLE,
    def: "Fully released. No caveats needed.",
    say: "Reference freely in decks, demos, and contracts — no qualifiers.",
    example: "Procedure Performs",
  },
];

export function Legend({ bare = false }: { bare?: boolean }) {
  return (
    <div
      className={
        bare
          ? "space-y-5 text-sm"
          : "space-y-5 rounded-lg border border-border bg-card/60 p-4 text-sm"
      }
    >
      {/* The two independent axes. */}
      <div className="grid grid-cols-1 gap-4">
        <LegendSection title="Access — who has it">
          <LegendItem
            badgeClass="bg-ea-soft text-ea"
            label="EA"
            heading="Early Access"
            body="On for a subset of workspaces, set by feature flag."
          />
          <LegendItem
            badgeClass="bg-ga-soft text-ga"
            label="GA"
            heading="General Availability"
            body="On by default for all workspaces."
          />
        </LegendSection>
        <LegendSection title="Readiness — how finished">
          <LegendItem
            badgeClass="bg-minor-soft text-minor"
            label="Beta"
            heading="Still evolving"
            body="Known gaps or failure modes we can’t fully predict — testing in the wild."
          />
          <LegendItem
            badgeClass="bg-border text-text-secondary"
            label="Stable"
            heading="Ready"
            body="Reliable and complete enough to meet expectations."
          />
        </LegendSection>
      </div>

      {/* The four states they combine into — each with how to talk about it. */}
      <div className="space-y-2">
        <h4 className="text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
          Every launch is one of four
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {COMBOS.map((c) => (
            <ComboCell key={`${c.access.label}-${c.readiness.label}`} combo={c} />
          ))}
        </div>
      </div>
    </div>
  );
}

const CHIP =
  "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider";

/** One cell of the 2×2: the chip pair, a plain definition, the customer
 *  talk-track, and the example feature pinned to the bottom so cells align. */
function ComboCell({ combo }: { combo: Combo }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-bg p-2.5">
      <div className="flex flex-wrap items-center gap-1">
        <span className={`${CHIP} ${combo.access.cls}`}>{combo.access.label}</span>
        <span className={`${CHIP} ${combo.readiness.cls}`}>{combo.readiness.label}</span>
      </div>
      <p className="text-[11.5px] leading-snug text-text-secondary">{combo.def}</p>
      <div className="flex gap-1.5 rounded-md border-l-2 border-accent bg-card px-2 py-1.5">
        <span className="pt-0.5 text-[9px] font-bold uppercase tracking-wider text-accent">
          Say
        </span>
        <span className="text-[11px] leading-snug text-text">{combo.say}</span>
      </div>
      <p className="mt-auto pt-0.5 text-[10px] italic text-text-tertiary">
        e.g. {combo.example}
      </p>
    </div>
  );
}

function LegendSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function LegendItem({
  badgeClass,
  label,
  heading,
  body,
}: {
  badgeClass: string;
  label: string;
  heading: string;
  body: string;
}) {
  return (
    <div className="flex gap-3">
      <span
        className={`h-fit rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${badgeClass}`}
      >
        {label}
      </span>
      <div className="min-w-0">
        <div className="font-medium text-text">{heading}</div>
        <p className="text-xs text-text-secondary">{body}</p>
      </div>
    </div>
  );
}
